import { Formik, Form } from "formik";
import React, { useEffect, useState } from "react";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import {
  ICFormikDropDown,
  ICFormikSwitch,
  ICFormikTextInput,
} from "../../core-component/formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { LOOKUP_CATEGORY_OPTION } from "../../utils/AppConstants";
import {
  fetchLookupId,
  LOOKUP_INITIAL_VALUES,
  handleSubmitLookup,
  ILookupForm,
  lookupValidationSchema,
  restoreLookup,
  IRestoreValue,
} from "./LookupFormController";
import { ILookup } from "./LookupListController";
import {
  hasAnyModification,
  prepareMessageFromParams,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";

interface ILookupFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  lookupToEdit?: ILookup;
}

const LookupForm: React.FC<ILookupFormProps> = ({
  handleClose,
  lookupToEdit,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [lookupFormInitialValues, setLookupFormInitialValues] =
    useState<ILookupForm>();
  const [lookupRestore, setLookupRestore] = useState<IRestoreValue>();
  const [submitValue, setSubmitValue] = useState<ILookupForm>();

  useEffect(() => {
    if (lookupToEdit) {
      getLookupById(lookupToEdit.id);
    } else {
      setLookupFormInitialValues(LOOKUP_INITIAL_VALUES);
    }
  }, []);

  const getLookupById = async (id: string) => {
    setLoading(true);
    await fetchLookupId(id, handleClose, setLookupFormInitialValues);
    setLoading(false);
  };

  const checkFormModification = (values: ILookupForm) => {
    if (
      lookupFormInitialValues &&
      hasAnyModification(values, lookupFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = async (values: ILookupForm) => {
    await handleSubmitLookup(
      values,
      setSubmitLoading,
      lookupToEdit?.id,
      handleClose,
      setLookupRestore,
      setSubmitValue
    );
  };
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {lookupFormInitialValues && (
            <Formik
              initialValues={lookupFormInitialValues}
              validationSchema={lookupValidationSchema(t)}
              onSubmit={(values) => handleSubmit(values)}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("lookup")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-scroll pt-2.5 mb-4 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"display_value"}
                          placeholder={t("display_value")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"data_value"}
                          placeholder={t("data_value")}
                          required
                          disabled={readOnly || !!lookupToEdit}
                        />
                        <ICFormikDropDown
                          {...formikPorps}
                          name={"lookup_category"}
                          placeholder={t("lookup_category")}
                          options={LOOKUP_CATEGORY_OPTION}
                          required
                          disabled={readOnly || !!lookupToEdit}
                        />
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"sort_order"}
                          placeholder={t("sort_order")}
                          type="number"
                          required
                          disabled={readOnly}
                        />

                        <ICFormikSwitch
                          {...formikPorps}
                          name={"is_active"}
                          containerClassName="gap-4 !w-fit"
                          placeholder={t("status")}
                          checkedValue={BIT_VALUE.One}
                          unCheckedValue={BIT_VALUE.Zero}
                          disabled={readOnly}
                        />
                      </div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {lookupRestore && (
            <ICConfirmationModal
              title={t("restore_title")}
              message={prepareMessageFromParams(t("restore_message"), [
                ["data_value", lookupRestore.existing_record_data_value],
                ["display_value", lookupRestore.existing_record_display_value],
              ])}
              onClose={(confirm) => {
                setLookupRestore(undefined);
                if (confirm && submitValue) {
                  restoreLookup(lookupRestore.id, submitValue, handleClose);
                }
              }}
            />
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default LookupForm;
